import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Pencil, Loader2 } from "lucide-react";

const STORAGE_KEY = "bmp_wizard_state";
const NOW = new Date();
const TERMS = (() => {
  const yr = NOW.getFullYear();
  return [`Fall ${yr}`, `Spring ${yr + 1}`, `Summer ${yr + 1}`, `Fall ${yr + 1}`];
})();
const STEPS = ["Where you are", "Where you're going", "What you'll study", "Your constraints", "Review & Generate"];

const emptyForm = {
  current_college_slug: "",
  current_college_other: "",
  current_gpa: "",
  total_credits: "",
  expected_transfer_term: TERMS[0],
  target_schools: [],           // [{institution_slug, fit_preference}]
  intended_majors: [],
  timeline_urgency: "6-12",
  financial_concern: "moderate",
  adviser_notes: "",
  email: "",
  first_name: "",
  phone: "",
};

export default function BuildMyPlan() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => Math.max(1, Math.min(5, parseInt(params.get("step") || "1", 10))));
  const [form, setForm] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); return s?.form || emptyForm; }
    catch { return emptyForm; }
  });
  const [ccs, setCCs] = useState([]);
  const [unis, setUnis] = useState([]);
  const [majors, setMajors] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setParams({ step: String(step) }, { replace: true }); }, [step, setParams]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, step })); }, [form, step]);

  useEffect(() => {
    api.get("/community-colleges", { params: { page_size: 100 } }).then((r) => setCCs((r.data.items || []).filter((i) => i.verification_status === "verified" || !i._placeholder))).catch(() => {});
    api.get("/universities", { params: { page_size: 100 } }).then((r) => setUnis(r.data.items || [])).catch(() => {});
    api.get("/majors", { params: { page_size: 100 } }).then((r) => setMajors(r.data.items || [])).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = (n) => {
    const e = {};
    if (n === 1) {
      if (!form.current_college_slug && !form.current_college_other.trim()) e.current_college = "Required";
      const gpa = parseFloat(form.current_gpa);
      if (!(gpa >= 0 && gpa <= 4)) e.current_gpa = "0.0–4.0";
      if (!Number.isInteger(parseInt(form.total_credits, 10)) || parseInt(form.total_credits, 10) < 0) e.total_credits = "Whole number";
    }
    if (n === 2 && (form.target_schools.length < 1 || form.target_schools.length > 5)) e.target_schools = "Pick 1–5";
    if (n === 3 && (form.intended_majors.length < 1 || form.intended_majors.length > 3)) e.intended_majors = "Pick 1–3 (or 'Undecided')";
    if (n === 4) {
      if (!form.first_name.trim()) e.first_name = "Required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => { if (validate(step)) setStep((s) => Math.min(5, s + 1)); };
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (![1, 2, 3, 4].every(validate)) return;
    setSubmitting(true);
    const payload = {
      current_college_slug: form.current_college_slug || null,
      current_college_other: form.current_college_other || null,
      current_gpa: parseFloat(form.current_gpa),
      total_credits: parseInt(form.total_credits, 10),
      expected_transfer_term: form.expected_transfer_term,
      target_schools: form.target_schools,
      intended_majors: form.intended_majors,
      timeline_urgency: form.timeline_urgency,
      financial_concern: form.financial_concern,
      adviser_notes: form.adviser_notes,
      email: form.email.trim().toLowerCase(),
      first_name: form.first_name.trim(),
      phone: form.phone,
    };
    try {
      const { data } = await api.post("/plans/generate", payload);
      localStorage.removeItem(STORAGE_KEY);
      navigate(`/my-plan/${data.plan_id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Try again or email us.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const groupedUnis = useMemo(() => ({
    fl: unis.filter((u) => (u.location?.state || "") === "FL"),
    out: unis.filter((u) => (u.location?.state || "") !== "FL"),
  }), [unis]);

  return (
    <Layout>
      <SEO title="Build My Plan — Personalized Florida Transfer Plan | Beyond Valencia" description="A 5-step guided intake that generates a personalized Florida transfer plan grounded in verified admissions data." canonical="/build-my-plan" />
      <section className="bg-bone" data-testid="build-my-plan-wizard">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 lg:py-16">

          <div className="text-[11px] uppercase tracking-[0.24em] text-brass font-medium">Build My Plan</div>
          <h1 className="mt-4 font-serif font-medium text-3xl sm:text-4xl lg:text-5xl text-ivy tracking-tight leading-[1.05]">Let&rsquo;s map your transfer.</h1>

          {/* Progress dots */}
          <div className="mt-8 flex items-center gap-3" data-testid="wizard-progress">
            {STEPS.map((_, i) => {
              const n = i + 1;
              const done = n < step, cur = n === step;
              return (
                <div key={n} className={`flex-1 h-2 border ${done ? "bg-brass border-brass" : cur ? "border-ivy" : "border-stone/40"}`} />
              );
            })}
          </div>
          <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-stone">Step {step} of 5 — {STEPS[step - 1]}</div>

          <div className="mt-10 border border-stone/40 bg-bone-dim/20 p-6 sm:p-8" data-testid={`wizard-step-${step}`}>
            {step === 1 && <Step1 form={form} update={update} ccs={ccs} terms={TERMS} errors={errors} />}
            {step === 2 && <Step2 form={form} update={update} groupedUnis={groupedUnis} errors={errors} />}
            {step === 3 && <Step3 form={form} update={update} majors={majors} errors={errors} />}
            {step === 4 && <Step4 form={form} update={update} errors={errors} />}
            {step === 5 && <Step5 form={form} setStep={setStep} ccs={ccs} unis={unis} majors={majors} />}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button onClick={goBack} disabled={step === 1 || submitting} className="text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ivy disabled:opacity-30 inline-flex items-center gap-1.5" data-testid="wizard-back"><ArrowLeft className="h-3 w-3" />Back</button>
            {step < 5 ? (
              <button onClick={goNext} className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6 inline-flex items-center gap-2" data-testid="wizard-next">Continue <ArrowRight className="h-3.5 w-3.5" /></button>
            ) : (
              <button onClick={submit} disabled={submitting} className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6 inline-flex items-center gap-2 disabled:opacity-50" data-testid="wizard-submit">
                {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating your plan…</> : <>Generate my plan <ArrowRight className="h-3.5 w-3.5" /></>}
              </button>
            )}
          </div>

          <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-stone">Grounded in verified admissions data. NEO cites official sources — nothing is fabricated.</p>
        </div>
      </section>
    </Layout>
  );
}

/* ============================= STEPS ============================= */

function Field({ label, error, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold mb-2">{label}</label>
      {children}
      {error && <div className="mt-1.5 text-[11px] text-crimson">{error}</div>}
    </div>
  );
}
const inputCls = "w-full border border-stone/40 bg-bone px-3 py-2.5 text-sm focus:border-ivy outline-none";

function Step1({ form, update, ccs, terms, errors }) {
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">Where you are now</h2>
      <Field label="Current community college" error={errors.current_college} testId="field-college">
        <select value={form.current_college_slug} onChange={(e) => update("current_college_slug", e.target.value)} className={inputCls} data-testid="input-college-slug">
          <option value="">Select…</option>
          {ccs.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          <option value="__other__">Other (type below)</option>
        </select>
        {form.current_college_slug === "__other__" && (
          <input type="text" placeholder="Your college" value={form.current_college_other} onChange={(e) => update("current_college_other", e.target.value)} className={`${inputCls} mt-2`} data-testid="input-college-other" />
        )}
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Current GPA (0.0–4.0)" error={errors.current_gpa}>
          <input type="number" step="0.1" min="0" max="4" value={form.current_gpa} onChange={(e) => update("current_gpa", e.target.value)} className={inputCls} data-testid="input-gpa" />
        </Field>
        <Field label="Total credits completed" error={errors.total_credits}>
          <input type="number" min="0" value={form.total_credits} onChange={(e) => update("total_credits", e.target.value)} className={inputCls} data-testid="input-credits" />
        </Field>
      </div>
      <Field label="Expected transfer term">
        <select value={form.expected_transfer_term} onChange={(e) => update("expected_transfer_term", e.target.value)} className={inputCls} data-testid="input-term">
          {terms.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
    </div>
  );
}

function Step2({ form, update, groupedUnis, errors }) {
  const toggle = (slug) => {
    const cur = form.target_schools;
    const found = cur.find((t) => t.institution_slug === slug);
    if (found) update("target_schools", cur.filter((t) => t.institution_slug !== slug));
    else if (cur.length < 5) update("target_schools", [...cur, { institution_slug: slug, fit_preference: "primary" }]);
  };
  const setFit = (slug, fit) => update("target_schools", form.target_schools.map((t) => t.institution_slug === slug ? { ...t, fit_preference: fit } : t));
  const isSel = (slug) => form.target_schools.some((t) => t.institution_slug === slug);
  const group = (title, items) => (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((u) => (
          <button key={u.slug} type="button" onClick={() => toggle(u.slug)} className={`text-[12px] px-3 py-1.5 border transition-colors ${isSel(u.slug) ? "bg-ivy text-bone border-ivy" : "bg-bone text-ink border-stone/40 hover:border-ivy"}`} data-testid={`target-toggle-${u.slug}`}>
            {u.name}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">Where you want to go</h2>
      <p className="text-sm text-ink-soft">Pick 1–5 target universities. You&rsquo;ll mark each as a primary or reach next.</p>
      {group("Florida", groupedUnis.fl)}
      {group("Beyond Florida", groupedUnis.out)}
      {errors.target_schools && <div className="text-[12px] text-crimson">{errors.target_schools}</div>}
      {form.target_schools.length > 0 && (
        <div className="pt-4 border-t border-stone/30 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Primary or reach?</div>
          {form.target_schools.map((t) => {
            const uni = [...groupedUnis.fl, ...groupedUnis.out].find((u) => u.slug === t.institution_slug);
            return (
              <div key={t.institution_slug} className="flex items-center justify-between gap-3 border border-stone/30 bg-bone p-3">
                <span className="text-sm text-ink">{uni?.name || t.institution_slug}</span>
                <div className="flex gap-2">
                  {["primary", "reach"].map((fit) => (
                    <button key={fit} onClick={() => setFit(t.institution_slug, fit)} className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border ${t.fit_preference === fit ? "bg-ivy text-bone border-ivy" : "border-stone/40 text-ink"}`} data-testid={`fit-${t.institution_slug}-${fit}`}>{fit}</button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Step3({ form, update, majors, errors }) {
  const toggle = (name) => {
    const cur = form.intended_majors;
    if (cur.includes(name)) update("intended_majors", cur.filter((m) => m !== name));
    else if (cur.length < 3) update("intended_majors", [...cur, name]);
  };
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">What you want to study</h2>
      <p className="text-sm text-ink-soft">Pick up to 3 intended majors, or select &ldquo;Undecided&rdquo;.</p>
      <div className="flex flex-wrap gap-2" data-testid="major-toggles">
        {[{ name: "Undecided", slug: "undecided" }, ...majors].map((m) => {
          const on = form.intended_majors.includes(m.name);
          return (
            <button key={m.slug || m.name} type="button" onClick={() => toggle(m.name)} className={`text-[12px] px-3 py-1.5 border transition-colors ${on ? "bg-ivy text-bone border-ivy" : "bg-bone text-ink border-stone/40 hover:border-ivy"}`} data-testid={`major-toggle-${m.slug || "undecided"}`}>
              {m.name}
            </button>
          );
        })}
      </div>
      {errors.intended_majors && <div className="text-[12px] text-crimson">{errors.intended_majors}</div>}
    </div>
  );
}

function Step4({ form, update, errors }) {
  const radio = (name, val, label) => (
    <label className={`inline-flex items-center gap-2 text-sm cursor-pointer border px-3 py-2 ${form[name] === val ? "bg-ivy text-bone border-ivy" : "border-stone/40 text-ink"}`}>
      <input type="radio" name={name} value={val} checked={form[name] === val} onChange={() => update(name, val)} className="hidden" data-testid={`radio-${name}-${val}`} />
      {label}
    </label>
  );
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">Your constraints</h2>
      <Field label="Timeline urgency">
        <div className="flex flex-wrap gap-2">{radio("timeline_urgency", ">12", ">12 months")}{radio("timeline_urgency", "6-12", "6–12 months")}{radio("timeline_urgency", "<6", "<6 months")}</div>
      </Field>
      <Field label="Financial concern level">
        <div className="flex flex-wrap gap-2">{radio("financial_concern", "none", "None")}{radio("financial_concern", "moderate", "Moderate")}{radio("financial_concern", "high", "High")}</div>
      </Field>
      <Field label="Anything else the adviser should know? (optional)">
        <textarea maxLength={500} rows={3} value={form.adviser_notes} onChange={(e) => update("adviser_notes", e.target.value)} className={inputCls} data-testid="input-notes" />
      </Field>
      <div className="pt-4 border-t border-stone/30 grid sm:grid-cols-2 gap-5">
        <Field label="First name" error={errors.first_name}>
          <input type="text" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className={inputCls} data-testid="input-firstname" />
        </Field>
        <Field label="Email" error={errors.email}>
          <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="input-email" />
        </Field>
        <Field label="Phone (optional)">
          <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} data-testid="input-phone" />
        </Field>
      </div>
    </div>
  );
}

function Step5({ form, setStep, ccs, unis, majors }) {
  const ccName = form.current_college_slug === "__other__" ? form.current_college_other : (ccs.find((c) => c.slug === form.current_college_slug)?.name || form.current_college_other || "—");
  const uniName = (slug) => unis.find((u) => u.slug === slug)?.name || slug;
  const row = (n, title, body) => (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-stone/20">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{title}</div>
        <div className="text-sm text-ink mt-1">{body}</div>
      </div>
      <button onClick={() => setStep(n)} className="text-stone hover:text-ivy p-1" aria-label={`Edit ${title}`} data-testid={`review-edit-step-${n}`}><Pencil className="h-3.5 w-3.5" /></button>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="wizard-review">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">Review & Generate</h2>
      {row(1, "Where you are", `${ccName} · GPA ${form.current_gpa} · ${form.total_credits} credits · Transfer ${form.expected_transfer_term}`)}
      {row(2, "Where you're going", form.target_schools.map((t) => `${uniName(t.institution_slug)} (${t.fit_preference})`).join(" · ") || "—")}
      {row(3, "What you'll study", form.intended_majors.join(" · ") || "—")}
      {row(4, "Constraints", `${form.timeline_urgency} timeline · ${form.financial_concern} financial · ${form.first_name} · ${form.email}`)}
    </div>
  );
}
